// src/components/BoardPack/ReportJobsWatcher.jsx
// Rendered once at the App level. Polls the finalize / export jobs this
// browser asked for and shows a toast when the server finishes (or fails)
// one, whatever screen the user is on. Exports get a Download button.
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiCall } from "@/api/apiCall";
import { getAccessToken } from "@/utils/authSession";
import {
  FINISHED_STATUSES,
  JOBS_CHANGED_EVENT,
  JOBS_UPDATED_EVENT,
  downloadJobFile,
  getWatchedJobs,
  setWatchedJobs,
} from "@/lib/reportJobs";

const ACTIVE_POLL_MS = 8000;
const IDLE_POLL_MS = 30000;

const ReportJobsWatcher = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    let timer = null;
    let stopped = false;

    const schedule = (ms) => {
      clearTimeout(timer);
      if (!stopped) timer = setTimeout(tick, ms);
    };

    const tick = async () => {
      const watched = getWatchedJobs();
      const ids = watched.map((entry) => entry.id);
      const loggedIn = !!getAccessToken();
      if (!ids.length || !loggedIn || window.location.pathname === "/board-pack-render") {
        schedule(IDLE_POLL_MS);
        return;
      }

      try {
        const res = await apiCall("get", "/reports/jobs", { ids: ids.join(",") }, null, false, false);
        if (res?.success && Array.isArray(res.data)) {
          const finished = res.data.filter((job) => FINISHED_STATUSES.includes(job.status));
          const known = new Set(res.data.map((job) => job.id));
          const finishedIds = new Set(finished.map((job) => job.id));

          finished.forEach((job) => {
            const name = `${job.contract} — ${job.month} ${job.year}`;
            const isExport = job.job_type === "export";
            if (job.status === "done" && isExport) {
              const type = watched.find((entry) => entry.id === job.id)?.download || "pdf";
              toast.success(`${name} export is ready`, {
                description: type === "pdf" ? "PDF document" : "PowerPoint presentation",
                duration: Infinity,
                closeButton: true,
                action: {
                  label: "Download",
                  onClick: () =>
                    downloadJobFile(job, type).catch(() => toast.error("Download failed, please export again")),
                },
              });
            } else if (job.status === "done") {
              toast.success(`${name} finalized`, {
                description: "The PDF and PowerPoint are ready.",
                duration: 15000,
                action: { label: "Open", onClick: () => navigateRef.current("/finalized-boardpacks") },
              });
            } else if (job.status === "failed") {
              toast.error(`${name} could not be ${isExport ? "exported" : "finalized"}`, {
                description: job.message || "Please try again.",
                duration: 15000,
              });
            }
          });

          // Read again: a new request may have been added while we were waiting
          setWatchedJobs(
            getWatchedJobs().filter(
              (entry) => !finishedIds.has(entry.id) && (known.has(entry.id) || !ids.includes(entry.id))
            )
          );
          window.dispatchEvent(new CustomEvent(JOBS_UPDATED_EVENT, { detail: res.data }));
        }
      } catch {
        /* try again on the next tick */
      }

      schedule(getWatchedJobs().length ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    };

    const onChanged = () => schedule(1500);
    window.addEventListener(JOBS_CHANGED_EVENT, onChanged);
    schedule(2000);

    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener(JOBS_CHANGED_EVENT, onChanged);
    };
  }, []);

  return null;
};

export default ReportJobsWatcher;
